/**
 * Who else is in here, and whether we are still listening.
 *
 * Presence is deliberately quiet: a stack of avatars that only exists when
 * someone else is actually connected, and a single dot for the stream itself.
 */

import { AvatarGroup, StatusDot, Tooltip, type AvatarPerson } from '@/components/ui';
import { relativeTime } from '@/lib/format';
import { useSession } from '@/lib/queries';
import { usePresence, useRealtime, type RealtimeStatus } from '@/lib/realtime';
import type { ID } from '@shared/types';

export interface PresenceBarProps {
  /** The signed-in user, so we do not show them their own avatar. */
  selfId?: ID | null;
  max?: number;
}

export function PresenceBar({ selfId, max = 4 }: PresenceBarProps) {
  const presence = usePresence();
  const others = presence.filter((user) => user.id !== selfId);
  if (others.length === 0) return null;

  const people: AvatarPerson[] = others.map((user) => ({ id: user.id, name: user.name }));
  const names = others.map((user) => user.name).join(', ');

  return (
    <Tooltip
      content={
        <span>
          {others.length === 1 ? `${names} is here` : `${names} are here`}
        </span>
      }
      side="bottom"
    >
      <div
        className="flex items-center"
        role="group"
        aria-label={`${others.length} teammate${others.length === 1 ? '' : 's'} online`}
      >
        <AvatarGroup users={people} max={max} size="sm" ringClassName="ring-[var(--surface-1)]" />
      </div>
    </Tooltip>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */

const STATUS_TONE: Record<RealtimeStatus, 'success' | 'warning' | 'danger' | 'muted'> = {
  idle: 'muted',
  connecting: 'warning',
  open: 'success',
  reconnecting: 'warning',
  offline: 'danger',
};

const STATUS_TITLE: Record<RealtimeStatus, string> = {
  idle: 'Live updates paused',
  connecting: 'Connecting to live updates…',
  open: 'Live',
  reconnecting: 'Reconnecting to live updates…',
  offline: 'Live updates disconnected',
};

/**
 * The realtime health indicator. Click to force a reconnect when it is down.
 *
 * It doubles as the session indicator: when `/auth/me` fails with anything other
 * than a 401 the app keeps the last known session and stays where it is, and
 * this is where it says so — non-blocking, in the header, instead of throwing
 * the user back to the sign-in screen.
 */
export function ConnectionDot() {
  const { status, lastEventAt, reconnect } = useRealtime();
  // Passive read: this renders inside the authenticated shell, and the route
  // guard already owns the fetch.
  const session = useSession({ enabled: false });
  const degraded = session.reconnecting;
  const tone = degraded ? 'warning' : STATUS_TONE[status];
  const live = status === 'open' && !degraded;
  const heard = lastEventAt ? relativeTime(lastEventAt) : null;
  const title = degraded ? 'Reconnecting to the server…' : STATUS_TITLE[status];

  return (
    <Tooltip
      side="bottom"
      content={
        <span className="flex flex-col gap-0.5">
          <span>{title}</span>
          <span className="text-xs text-muted">
            {degraded
              ? 'Your session is still open — retrying in the background.'
              : live && heard
                ? `Last event ${heard} · click to resync`
                : 'Click to reconnect now'}
          </span>
        </span>
      }
    >
      <button
        type="button"
        onClick={() => {
          reconnect();
          if (degraded) void session.refetch();
        }}
        aria-label={`Live updates: ${title}. Reconnect.`}
        className={[
          'flex size-[var(--h-control-sm)] items-center justify-center rounded-[var(--radius-sm)]',
          'transition-colors ease-[var(--ease-out)] duration-[var(--dur-fast)]',
          'hover:bg-[var(--surface-hover)] active:bg-[var(--surface-active)]',
          'focus-visible:outline-2 focus-visible:outline-offset-1 outline-[color:var(--focus)]',
        ].join(' ')}
      >
        <StatusDot tone={tone} pulse={live || degraded} size="sm" />
      </button>
    </Tooltip>
  );
}
